import React, { useState, useEffect, useMemo } from 'react';
import { X, Send, FileText, PenLine, UserCheck } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

/**
 * Przekazanie faktury z niepodpisanym załącznikiem.
 *
 * Gdy załączniki merytorycznie dotyczą innej osoby, użytkownik nie musi ich podpisywać —
 * wskazuje jedną osobę odpowiedzialną za podpis, a faktura jest od razu do niej przekazywana.
 * Oświadczenie trafia do historii faktury i obowiązuje wyłącznie dla osoby, która je złożyła.
 *
 * Wszystkie załączniki faktury trafiają do tej samej wskazanej osoby — nie ma wskazywania
 * osobnego pracownika dla każdego załącznika.
 *
 * @param {Array<{id: number, name: string}>} attachments załączniki blokujące przekazanie
 * @param {Array} users lista użytkowników CRM do wskazania
 * @param {object} currentUser bieżący użytkownik (nie może wskazać sam siebie)
 * @param {function} onSign wywoływane, gdy użytkownik wybiera samodzielny podpis załącznika
 * @param {function} onSuccess (designatedSignerUserId) — zapisano wskazanie, można przekazać fakturę
 */
const AttachmentSignWaiverModal = ({
  isOpen,
  onClose,
  invoiceId,
  invoiceNumber,
  attachments = [],
  users = [],
  currentUser,
  onSign,
  onSuccess,
}) => {
  const [designatedSignerUserId, setDesignatedSignerUserId] = useState(null);
  const [reason, setReason] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setDesignatedSignerUserId(null);
      setReason('');
      setSearchQuery('');
    }
  }, [isOpen]);

  const selectableUsers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return users.filter((u) => {
      if (u.active === false) return false;
      if (currentUser?.id != null && Number(u.id) === Number(currentUser.id)) return false;
      if (!query) return true;
      const fullName = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
      return fullName.includes(query) || (u.email || '').toLowerCase().includes(query);
    });
  }, [users, searchQuery, currentUser]);

  if (!isOpen) {
    return null;
  }

  const userLabel = (u) => (u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.email);

  const handleSubmit = async () => {
    if (designatedSignerUserId == null || saving) {
      return;
    }
    setSaving(true);
    try {
      await api.post(`/invoices/${invoiceId}/attachment-sign-waivers`, {
        waivers: attachments.map((att) => ({
          attachmentId: att.id,
          designatedSignerUserId,
          reason: reason.trim() || null,
        })),
      });
      // Przekazanie faktury wykonuje strona wywołująca (obsługa zastępstw po stronie backendu)
      await onSuccess?.(designatedSignerUserId);
    } catch (error) {
      const message = error?.response?.data?.error;
      toast.error(message || 'Nie udało się zapisać wskazania osoby podpisującej');
      console.error('Error saving attachment sign waivers:', error);
    } finally {
      setSaving(false);
    }
  };

  const selectedUser = users.find((u) => Number(u.id) === Number(designatedSignerUserId));
  const many = attachments.length > 1;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] backdrop-blur-sm p-3 sm:p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full border border-gray-200 max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                {many ? 'Załączniki bez podpisu' : 'Załącznik bez podpisu'}
              </h3>
              {invoiceNumber && (
                <p className="text-xs sm:text-sm text-gray-500 mt-1">
                  Faktura: <span className="font-semibold text-gray-700">{invoiceNumber}</span>
                </p>
              )}
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Lista załączników, których dotyczy wskazanie */}
          <div className="mb-4 border border-gray-200 rounded-lg divide-y divide-gray-100">
            {attachments.map((att) => (
              <div key={att.id} className="flex items-center gap-2 px-3 py-2">
                <FileText className="h-4 w-4 text-gray-500 flex-shrink-0" />
                <span className="text-sm text-gray-900 break-all">{att.name}</span>
              </div>
            ))}
          </div>

          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-xs sm:text-sm text-amber-900">
              {many
                ? 'Wskaż osobę, która podpisze te załączniki. Faktura zostanie od razu do niej przekazana, a wskazanie trafi do historii faktury.'
                : 'Wskaż osobę, która podpisze ten załącznik. Faktura zostanie od razu do niej przekazana, a wskazanie trafi do historii faktury.'}
            </p>
          </div>

          {onSign && (
            <button
              onClick={() => onSign(attachments[0])}
              className="w-full mb-4 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <PenLine className="h-4 w-4" />
              {many ? 'Podpiszę załączniki sam' : 'Podpiszę załącznik sam'}
            </button>
          )}

          <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
            {many ? 'Kto podpisze załączniki:' : 'Kto podpisze załącznik:'}
          </label>

          <input
            type="text"
            placeholder="Wyszukaj użytkownika..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm mb-2"
          />

          <div className="border border-gray-300 rounded-lg max-h-60 overflow-y-auto">
            {selectableUsers.map((u) => {
              const isSelected = Number(u.id) === Number(designatedSignerUserId);
              return (
                <div
                  key={u.id}
                  onClick={() => setDesignatedSignerUserId(u.id)}
                  className={`px-3 py-2 cursor-pointer hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                    isSelected ? 'bg-blue-100' : ''
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900">{userLabel(u)}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{u.email}</div>
                    </div>
                    {isSelected && <UserCheck className="h-5 w-5 text-blue-600 flex-shrink-0" />}
                  </div>
                </div>
              );
            })}
            {selectableUsers.length === 0 && (
              <div className="px-3 py-4 text-sm text-gray-500 text-center">
                Brak użytkowników pasujących do wyszukiwania
              </div>
            )}
          </div>

          <input
            type="text"
            placeholder="Komentarz (opcjonalnie), np. podpisuje kierownik apteki"
            value={reason}
            maxLength={500}
            onChange={(e) => setReason(e.target.value)}
            className="w-full mt-3 px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          />

          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Anuluj
            </button>
            <button
              onClick={handleSubmit}
              disabled={designatedSignerUserId == null || saving}
              className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              <Send className="h-4 w-4" />
              {saving
                ? 'Przekazywanie...'
                : selectedUser
                  ? `Przekaż do: ${userLabel(selectedUser)}`
                  : 'Wskaż osobę i przekaż'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttachmentSignWaiverModal;
